/* eslint-disable no-restricted-syntax -- amber token for failed state, theme has no warning colour */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle2, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { useIsPublishingEnabled } from '@utils/api/connections/use-is-publishing-enabled.tsx';
import {
  listRememberedPublishJobs,
  forgetPublishJob,
  type RememberedPublishJob
} from '@utils/localStorage/publish-job-storage.ts';
import { useGetPublishJob } from '@utils/api/publishing/use-get-publish-job.tsx';
import { isTerminalPublishStatus } from '@utils/types/publishing/publish.ts';
import { getPlatformSpec } from '@utils/types/connections/platforms.ts';
import { PATHS } from '../../router/paths.ts';

const WARNING_ICON = '#B45309';

interface Props {
  /** Milliseconds between scans of localStorage for new jobs. */
  scanIntervalMs?: number;
}

/**
 * Top-level mount that polls every publish job the user has in flight
 * across documents. Renders a floating pill at bottom-right when any
 * job is running, with a link back to the originating editor. On
 * terminal transitions it fires a toast and clears the localStorage key.
 */
export default function PublishJobsWatcher({
  scanIntervalMs = 15000
}: Props = {}) {
  const { isEnabled: isPublishingEnabled } = useIsPublishingEnabled();
  const [jobs, setJobs] = useState<RememberedPublishJob[]>(() =>
    listRememberedPublishJobs()
  );

  // Periodic rescan so a job started from another document / tab is
  // picked up without a reload. We rescan on an interval instead of
  // listening for storage events because same-tab writes don't fire a
  // storage event, so we'd miss our own writes.
  useEffect(() => {
    if (!isPublishingEnabled) return;
    const handle = setInterval(() => {
      setJobs(prev => {
        const next = listRememberedPublishJobs();
        if (next.length !== prev.length) return next;
        for (let i = 0; i < next.length; i += 1) {
          if (next[i].jobId !== prev[i].jobId) return next;
        }
        return prev;
      });
    }, scanIntervalMs);
    return () => clearInterval(handle);
  }, [isPublishingEnabled, scanIntervalMs]);

  if (!isPublishingEnabled || jobs.length === 0) return null;

  return (
    <ul
      aria-live="polite"
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
    >
      {jobs.map(job => (
        <WatchedPublishJob
          key={job.jobId}
          job={job}
          onTerminal={() => {
            forgetPublishJob(job.sessionId, job.documentId);
            setJobs(prev => prev.filter(j => j.jobId !== job.jobId));
          }}
        />
      ))}
    </ul>
  );
}

function WatchedPublishJob({
  job,
  onTerminal
}: {
  job: RememberedPublishJob;
  onTerminal: () => void;
}) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const location = useLocation();
  const { data } = useGetPublishJob(job.jobId);

  const editorPath = useMemo(
    () =>
      // Backend decides whether the document is a transcript or captions;
      // the editor itself redirects, so TRANSCRIPT is a safe default route.
      PATHS(job.sessionId, job.documentId).TRANSCRIPT,
    [job.documentId, job.sessionId]
  );
  const alreadyOnThisDocument = location.pathname.includes(job.documentId);

  useEffect(() => {
    if (!data) return;
    if (!isTerminalPublishStatus(data.status)) return;
    const spec = getPlatformSpec(data.platform);
    const platformLabel = spec?.displayName ?? data.platform;
    if (data.status === 'succeeded') {
      toast.success(t('publish-watcher-success', { platform: platformLabel }), {
        duration: 6000
      });
    } else if (data.status === 'failed') {
      toast.error(t('publish-watcher-failed', { platform: platformLabel }), {
        duration: 8000
      });
    }
    onTerminal();
  }, [data, onTerminal, t]);

  if (!data || isTerminalPublishStatus(data.status)) return null;
  // Suppress the floating pill while the user is on the originating
  // document — the inline dialog is already surfacing progress there.
  if (alreadyOnThisDocument) return null;

  const spec = getPlatformSpec(data.platform);
  const platformLabel = spec?.displayName ?? data.platform;

  const statusGlyph =
    data.status === 'queued' || data.status === 'running' ? (
      <Loader2
        className="h-4 w-4 animate-spin"
        aria-hidden="true"
        style={{ color: getColour('activeText') }}
      />
    ) : data.status === 'succeeded' ? (
      <CheckCircle2
        className="h-4 w-4"
        aria-hidden="true"
        style={{ color: getColour('success') }}
      />
    ) : (
      <AlertCircle
        className="h-4 w-4"
        aria-hidden="true"
        style={{ color: WARNING_ICON }}
      />
    );

  return (
    <li>
      <Link
        to={editorPath}
        className="inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm shadow-sm outline-none focus-visible:ring-2"
        style={{
          borderColor: getColour('hairline'),
          background: getColour('secondLayer'),
          color: getColour('normalText')
        }}
      >
        {statusGlyph}
        <span>{t('publish-watcher-pill', { platform: platformLabel })}</span>
        <ExternalLink
          className="h-3.5 w-3.5"
          aria-hidden="true"
          style={{ color: getColour('inactiveText') }}
        />
      </Link>
    </li>
  );
}
